import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, CreditCard, ShieldCheck } from 'lucide-react';

// Billing setup landing for Google-OAuth signups.
// Reads `?token=...` from the magic link in the approval email, exchanges
// it for a session JWT via POST /api/auth/billing-setup, then routes the
// user straight to /pricing to start their 14-day Stripe trial.
//
// Email/password users never receive a billing-setup token and therefore
// never land here; their flow is unchanged.
export default function BillingSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithBillingSetupToken } = useAuth();
  const setupToken = params.get('token');
  const [error, setError] = useState(
    setupToken ? null : 'This link is missing its token. Ask an admin to re-approve your account.'
  );
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    if (!setupToken) return;
    hasProcessed.current = true;

    (async () => {
      try {
        await loginWithBillingSetupToken(setupToken);
        toast.success('Account activated — choose a plan to start your 14-day trial.');
        navigate('/pricing', { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || 'This setup link is invalid or has already been used.';
        setError(typeof detail === 'string' ? detail : 'Setup link error');
      }
    })();
  }, [setupToken, navigate, loginWithBillingSetupToken]);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background:
          'radial-gradient(circle at 30% 20%, rgba(184,155,94,0.08), transparent 55%), linear-gradient(180deg, #F5F1E8 0%, #FFFFFF 100%)',
      }}
      data-testid="billing-setup-page"
    >
      <div className="max-w-md w-full rounded-sm border border-[#E8E6E1] bg-white/80 backdrop-blur p-8 shadow-[0_18px_40px_rgba(31,41,55,0.06)]">
        {error ? (
          <div className="space-y-5 text-center" data-testid="billing-setup-error">
            <div className="text-xs uppercase tracking-[0.2em] text-rose-600">Setup link error</div>
            <h1 className="font-serif text-2xl text-stone-800">We couldn&apos;t activate your account</h1>
            <p className="text-sm text-stone-600">{error}</p>
            <button
              type="button"
              className="text-xs uppercase tracking-widest text-[#B89B5E] hover:underline"
              onClick={() => navigate('/login', { replace: true })}
              data-testid="billing-setup-error-back"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="space-y-5 text-center">
            <div className="flex justify-center">
              <Loader2 className="w-7 h-7 animate-spin text-[#B89B5E]" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500">
              Validating your approval link
            </div>
            <h1 className="font-serif text-2xl text-stone-800">Activating your account…</h1>
            <p className="text-sm text-stone-600 max-w-sm mx-auto">
              You&apos;ll choose a plan and start your 14-day trial next. No charge for 14 days · cancel anytime.
            </p>
            <div className="flex flex-col gap-2 rounded-sm border border-[#E8E6E1] bg-white/60 p-3 text-left">
              <div className="flex items-center gap-2 text-xs text-stone-700">
                <CreditCard className="h-3.5 w-3.5 text-[#B89B5E]" />
                <span>Card collected on next step via Stripe</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-stone-700">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                <span>No charge until trial ends — cancel anytime</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
